// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Shipment, getShipmentType } from '@/shipment/api/types';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import { getShipmentTracking } from '@/tracking/api/get-shipment-tracking';
import { TrackingData } from '@/tracking/api/types';
import { useQuery } from '@tanstack/react-query';

export type ShipmentThirdPartySerialWarningPhase = 'shipped' | 'delivered';
export type ShipmentThirdPartySerialWarningPhases =
  readonly ShipmentThirdPartySerialWarningPhase[];

const TRACKING_STATUS_DELIVERED = 'delivered';
const TRACKING_STATUS_TRANSIT = 'transit';
const NO_WARNING_PHASES: ShipmentThirdPartySerialWarningPhases = [];
const SHIPPED_WARNING_PHASES: ShipmentThirdPartySerialWarningPhases = [
  'shipped',
];
const DELIVERED_WARNING_PHASES: ShipmentThirdPartySerialWarningPhases = [
  'delivered',
];
const SHIPPED_AND_DELIVERED_WARNING_PHASES: ShipmentThirdPartySerialWarningPhases =
  ['shipped', 'delivered'];

function getPackageStatusRequiringSerialConfirmation(
  shipment: Shipment,
  trackingData: TrackingData
): ShipmentThirdPartySerialWarningPhases {
  let hasTransitEvent = false;

  for (const event of trackingData.shippingHistory) {
    const status = event.status.toLowerCase();

    if (status === TRACKING_STATUS_DELIVERED) {
      if (shipment.status === 'open') {
        return SHIPPED_AND_DELIVERED_WARNING_PHASES;
      }

      return shipment.status === 'shipped'
        ? DELIVERED_WARNING_PHASES
        : NO_WARNING_PHASES;
    }

    if (status === TRACKING_STATUS_TRANSIT) {
      hasTransitEvent = true;
    }
  }

  if (hasTransitEvent && shipment.status === 'open') {
    return SHIPPED_WARNING_PHASES;
  }

  return NO_WARNING_PHASES;
}

function hasUnconfirmedSerials(shipment: Shipment): boolean {
  return shipment.items.some((lineItem) => {
    if (!shipmentLineAllowsSerials(lineItem.item)) {
      return false;
    }

    if (lineItem.pendingSerialCount > 0) {
      return true;
    }

    return (
      lineItem.quantity !== null && lineItem.serialCount < lineItem.quantity
    );
  });
}

export function useShipmentThirdPartySerialWarningPhases(
  shipment: Shipment
): ShipmentThirdPartySerialWarningPhases {
  // A shipment's provider is its binding, not the origin location's 3PL flag
  const shouldCheckTracking =
    getShipmentType(shipment) === 'outbound' &&
    shipment.thirdPartyBound &&
    hasUnconfirmedSerials(shipment) &&
    Boolean(
      shipment.trackingNumberDetails?.carrier &&
        shipment.trackingNumberDetails.trackingNumber
    );

  const { data } = useQuery({
    queryKey: ['shipment', shipment.key, 'tracking'],
    queryFn: () => getShipmentTracking(shipment.key),
    enabled: shouldCheckTracking,
  });

  if (!shouldCheckTracking || !data?.data) {
    return NO_WARNING_PHASES;
  }

  return getPackageStatusRequiringSerialConfirmation(shipment, data.data);
}
